<template>
  <div :id="id" class="iconn-button" :class="iconnClasses" @click="handleClick">
    <template v-if="loading">
      <div class="skeleton-label iconn-skeleton" :style="calculateTextWidth"></div>
    </template>
    <template v-else>
      <div v-if="pending" class="pending-container">
        <div class="pending" />
      </div>
      <slot name="left">
        <IconMaterial
          v-if="iconLeft"
          :icon="iconLeft"
          :variant="iconLeftVariant"
          :color="iconLeftColor"
          :size="iconLeftSizeValue"
          :disabled="disabled"
          @click.stop="clickLeftIconHandler"
        />
      </slot>
      <slot>
        <span v-if="text" class="iconn-button_text">
          {{ text }}
        </span>
      </slot>
      <slot name="right">
        <IconMaterial
          v-if="iconRight"
          :icon="iconRight"
          :variant="iconRightVariant"
          :color="iconRightColor"
          :size="iconRightSizeValue"
          :disabled="disabled"
          @click.stop="clickRightIconHandler"
        />
      </slot>
    </template>
  </div>
</template>

<script setup lang="ts">
import IconMaterial from './IconMaterial.vue';
import { computed, getCurrentInstance } from 'vue';

const instance = getCurrentInstance();
export type Sizes = 'sm' | 'md' | 'lg';

export type Variant =
  | 'primary'
  | 'primary-flat'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'success-outline'
  | 'error'
  | 'error-outline'
  | 'warning'
  | 'warning-outline';

export interface ButtonProps {
  id?: string;
  text?: string;
  iconLeft?: string;
  iconLeftColor?: string;
  iconLeftSize?: number;
  iconLeftVariant?: string;
  iconRight?: string;
  iconRightColor?: string;
  iconRightSize?: number;
  iconRightVariant?: string;
  variant?: Variant;
  size?: Sizes;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  pending?: boolean;
}

const props = withDefaults(defineProps<ButtonProps>(), {
  variant: 'primary',
  size: 'sm',
  fullWidth: false,
  disabled: false,
  loading: false,
  pending: false,
  iconRightColor: '#64748B',
  iconRightSize: 16,
  iconRightVariant: 'outlined',
  iconLeftColor: '#64748B',
  iconLeftSize: 16,
  iconLeftVariant: 'outlined',
});

const iconSizeMap: Record<string, number> = {
  sm: 16,
  lg: 20,
};

const calculateTextWidth = computed(() => {
  if (!props.text) return 'width: 0px';

  const fontSize = 12;
  const charWidthRatio = 0.55; /// average character size
  const textLength = props.text.length;

  return `width: ${textLength * charWidthRatio * fontSize}px`;
});

const iconLeftSizeValue = computed(() => {
  if (props.iconLeftSize) {
    return props.iconLeftSize;
  }
  return iconSizeMap[props.size];
});

const iconRightSizeValue = computed(() => {
  if (props.iconRightSize) {
    return props.iconRightSize;
  }
  return iconSizeMap[props.size];
});

const iconnClasses = computed(() => {
  const variantClass = props.variant ? `iconn_${props.variant}` : 'iconn_primary';
  const sizeClass = `iconn_${props.size}`;
  const fullWidthClass = props.fullWidth ? 'iconn_full-width' : '';
  const disabledClass = props.disabled ? 'disabled' : '';
  return [variantClass, sizeClass, fullWidthClass, disabledClass];
});

const emit = defineEmits(['click', 'click-left-icon', 'click-right-icon']);

const handleClick = (event: MouseEvent) => {
  if (props.disabled || props.pending) {
    return;
  }
  emit('click', event);
};

const clickLeftIconHandler = (event: MouseEvent) => {
  if (props.disabled || props.pending) {
    return;
  }
  if (!instance?.vnode?.props?.onClickLeftIcon) {
    handleClick(event);
    return;
  }

  emit('click-left-icon');
};
const clickRightIconHandler = (event: MouseEvent) => {
  if (props.disabled || props.pending) {
    return;
  }
  if (!instance?.vnode?.props?.onClickRightIcon) {
    handleClick(event);
    return;
  }
  emit('click-right-icon');
};
</script>

<style scoped lang="scss">
@import '@foundation';
.iconn-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  &.iconn {
    &_full-width {
      width: 100%;
    }
    // Sizes
    &_sm {
      @extend .medium-caption-md;
      height: size(7);
      //Todo: change after have new foundation classes
      padding: size(3) size(3);
      line-height: normal;
    }
    &_md {
      @extend .medium-caption-md;
      height: size(7);
      //Todo: change after have new foundation classes
      padding: size(3) size(6);
      line-height: normal;
    }
    &_lg {
      @extend .medium-body-sm;
      height: size(8);
      //Todo: change after have new foundation classes
      padding: size(3) size(4);
      line-height: normal;
    }
    // Variants
    &_primary {
      --pending-color: #{map-get($basic, 'white')};
      color: var(--pending-color);
      background-color: map-get($primary, 'purple-500');
      outline: 1px solid map-get($primary, 'purple-100');
      &:hover:not(.disabled) {
        background-color: map-get($primary, 'purple-600');
      }
      &:active:not(.disabled) {
        outline: 2px solid map-get($primary, 'purple-400');
        background-color: map-get($primary, 'purple-700');
      }
      &.disabled {
        @extend .typography_light;
        cursor: not-allowed;
        outline: 1px solid map-get($neutral, 'neutral-50');
        background-color: map-get($neutral, 'neutral-300');
      }
      line-height: normal;
    }
    &_primary-flat {
      --pending-color: #{map-get($neutral, 'neutral-900')};
      color: var(--pending-color);
      @extend .background_neutral_white;
      outline: 1px solid map-get($neutral, 'neutral-300');
      &:active {
        outline: 2px solid map-get($neutral, 'neutral-300');
      }
      &.disabled {
        @extend .typography_light;
        cursor: not-allowed;
        outline: 1px solid map-get($neutral, 'neutral-50');
        background-color: map-get($neutral, 'neutral-300');
      }
      line-height: normal;
    }
    &_secondary {
      --pending-color: #{map-get($neutral, 'neutral-900')};
      color: var(--pending-color);
      @extend .background_neutral_white;
      outline: 1px solid map-get($neutral, 'neutral-300');
      &:hover:not(.disabled) {
        @extend .background_neutral_secondary;
      }
      &:active:not(.disabled) {
        outline-width: 2px;
      }
      &.disabled {
        @extend .typography_disabled;
        cursor: not-allowed;
      }
      line-height: normal;
    }
    &_tertiary {
      --pending-color: #{map-get($primary, 'purple-600')};
      color: var(--pending-color);
      background-color: map-get($primary, 'purple-50');
      &:hover:not(.disabled) {
        outline: 1px solid map-get($primary, 'purple-600');
      }
      &:active:not(.disabled) {
        outline-width: 2px;
      }
      &.disabled {
        @extend .typography_disabled;
        cursor: not-allowed;
        background-color: map-get($neutral, 'neutral-50');
      }
      line-height: normal;
    }
    &_success {
      --pending-color: #{map-get($success, 'green-800')};
      color: var(--pending-color);
      @extend .background_positive_subtle;
      &:hover:not(.disabled) {
        outline: 1px solid map-get($success, 'green-800');
      }
      &:active:not(.disabled) {
        outline-width: 2px;
      }
      line-height: normal;
    }
    &_success-outline {
      --pending-color: #{map-get($success, 'green-800')};
      color: var(--pending-color);
      outline: 1px solid map-get($success, 'green-200');
      background-color: map-get($success, 'green-50') !important;
      &:hover:not(.disabled) {
        outline: 1px solid map-get($success, 'green-200');
      }
      &:active:not(.disabled) {
        outline-width: 2px;
      }
      line-height: normal;
    }
    &_warning-outline {
      --pending-color: #{map-get($warning, 'orange-700')};
      color: var(--pending-color);
      outline: 1px solid map-get($warning, 'orange-200');
      background-color: map-get($warning, 'orange-50') !important;
      &:hover:not(.disabled) {
        outline: 1px solid map-get($warning, 'orange-200');
      }
      &:active:not(.disabled) {
        outline-width: 2px;
      }
      line-height: normal;
    }
    &_error {
      --pending-color: #{map-get($error, 'red-600')};
      color: var(--pending-color);
      @extend .background_negative_subtle;
      &:hover:not(.disabled) {
        outline: 1px solid map-get($error, 'red-600');
      }
      &:active:not(.disabled) {
        outline-width: 2px;
      }
      line-height: normal;
    }
    &_error-outline {
      --pending-color: #{map-get($error, 'red-600')};
      color: var(--pending-color);
      outline: 1px solid map-get($error, 'red-200');
      background-color: map-get($error, 'red-50') !important;
      &:hover:not(.disabled) {
        outline: 1px solid map-get($error, 'red-200');
      }
      &:active:not(.disabled) {
        outline-width: 2px;
      }
      line-height: normal;
    }
  }
  &_text {
    white-space: nowrap;
    user-select: none;
    line-height: normal;
  }
  .pending {
    &-container {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: inherit;
      border-radius: inherit;
    }
    width: 20px;
    aspect-ratio: 2;
    z-index: 3;
    --pending-gradient: no-repeat
      radial-gradient(circle closest-side, var(--pending-color) 90%, #0000);
    background:
      var(--pending-gradient) 0 50%,
      var(--pending-gradient) 50% 50%,
      var(--pending-gradient) 100% 50%;
    background-size: calc(100% / 3) 50%;
    animation: pending-animation 1s infinite linear;
  }
  @keyframes pending-animation {
    20% {
      background-position:
        0 0,
        50% 50%,
        100% 50%;
    }
    40% {
      background-position:
        0 100%,
        50% 0,
        100% 50%;
    }
    60% {
      background-position:
        0 50%,
        50% 100%,
        100% 0;
    }
    80% {
      background-position:
        0 50%,
        50% 50%,
        100% 100%;
    }
  }
}
.iconn-skeleton::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  height: 100%;
  width: 100%;
  background: linear-gradient(
    to right,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%
  );
  animation: loading-shimmer 1.5s infinite;
}

@keyframes loading-shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
.skeleton-label {
  height: 16px;
  background-color: map-get($neutral, 'neutral-100');
  border-radius: 24px;
  position: relative;
  overflow: hidden;
}
</style>
